import {
  SECTION_DEFAULT_FILL,
  TABLE_BORDER_PX,
  TABLE_CELL_PADDING_EM,
  compareCanvasObjectsForRender,
  isDrawableObject,
  resolveTableObjectColumnWidths,
  resolveTableObjectRowHeights,
  type ArrowCorner,
  type ArrowEdge,
  type CanvasFrame,
  type CanvasObject,
  type EmojiStampKind,
} from "@refinement-canvas/domain";
import { CANVAS_GRID_PX, CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "./canvas-coordinates";
import {
  computeConnectorGeometry,
  connectorFrameOf,
  connectorGeometryInputFor,
  drawConnectorGeometry,
} from "./connector-geometry";
import { preloadImages, type LinkCardImageResolver } from "./canvas-raster-images";
import { computeStickyExportFontPx } from "./canvas-sticky-text-metrics";
import {
  drawSingleTextBar,
  drawTableSkeleton,
  drawTextSkeleton,
  textBarHeightFor,
} from "./canvas-text-skeleton";
import { MARKDOWN_TABLE_BORDER_COLOR, tableObjectCellDisplayText } from "./markdown-text";
import { resolveArrowEdgePath, resolveArrowPath } from "./shape-arrow";
import { resolveRectCornerRadius, traceRoundedRectPath } from "./shape-rect";
import { CONNECTOR_DEFAULT_STROKE } from "./connector-style";
import {
  STICKY_LINE_HEIGHT_RATIO,
  STICKY_PADDING_PX,
  computeStickyBodyReservePx,
  computeStickySignatureFontPx,
} from "./sticky-font-fit";
import { resolveStickyTextPalette } from "./sticky-text-color";
import { isSnapshotAvatarImageUrl } from "../auth/avatar-image-url";
import { resolveTextBackground, TEXT_DEFAULT_COLOR } from "./text-color";
import { safeHostname } from "./url-hostname";
import {
  applyCanvasStrokePattern,
  fillDottedStrokeStartFallback,
  resolveStrokePattern,
} from "./stroke-pattern";
import { displayTextOfLinkifiedText } from "./linkify-text";

// 表示枠は 16:9 で、ダッシュボードのグリッド（minmax(240px, 1fr) の auto-fill）により
// 1 列あたり最大およそ 480 CSS px になる。DPR 2 の端末では約 960 デバイス px へ拡大される
// ため、その幅でラスタライズする。
// devicePixelRatio を掛ける方式は採らない。サムネイルは生成した端末ではなく、S3 経由で
// 他の利用者の端末に表示されるため、生成側の DPR に合わせると低 DPI 端末で作られた
// サムネイルが高 DPI 端末でぼやけたままになる。
export const THUMBNAIL_WIDTH = 960;
export const THUMBNAIL_HEIGHT = Math.round((CANVAS_HEIGHT_PX / CANVAS_WIDTH_PX) * THUMBNAIL_WIDTH);

// グリッドのドット間隔（ラスタ px）。解像度を上げてもドットが密にならないよう、
// 基準幅 320 に対する倍率で追従させて画面上の密度を従来どおりに保つ。
const GRID_DOT_STEP_PX = 24 * (THUMBNAIL_WIDTH / 320);
// ドットの濃さ。内容の邪魔にならない程度まで下げる。
const GRID_DOT_COLOR = "rgba(26, 26, 46, 0.05)";

/** セクションのタイトルバーの高さ（stage px。section-object.tsx の TITLE_BAR_HEIGHT_PX と一致させる）。 */
const SECTION_TITLE_BAR_HEIGHT_PX = 32;
/** セクションのタイトルバーの左右余白（stage px。section-object.tsx の padding: 0 10px と一致させる）。 */
const SECTION_TITLE_PADDING_X_PX = 10;
/** セクションのタイトルの文字サイズ（stage px。section-object.tsx の fontSize: 16px と一致させる）。 */
const SECTION_TITLE_FONT_PX = 16;
/** セクションのタイトルの文字色（section-object.tsx の color: #1a1a2e と一致させる）。 */
const SECTION_TITLE_COLOR = "#1a1a2e";

/** リンクカードの内側余白（stage px。link-card-object.tsx の padding: 12px と一致させる）。 */
const LINK_CARD_PADDING_PX = 12;
/** リンクカードの要素間の間隔（stage px。link-card-object.tsx の gap: 4px と一致させる）。 */
const LINK_CARD_GAP_PX = 4;
/** リンクカードの画像領域が占める高さの比（link-card-object.tsx の height: 55% と一致させる）。 */
const LINK_CARD_IMAGE_HEIGHT_RATIO = 0.55;
/** リンクカードの 3 要素の文字サイズ（stage px。link-card-object.tsx の各 fontSize と一致させる）。 */
const LINK_CARD_TITLE_FONT_PX = 14;
const LINK_CARD_DESCRIPTION_FONT_PX = 12;
const LINK_CARD_SITE_NAME_FONT_PX = 11;
/** リンクカードのタイトル帯の色（従来のサムネイルのタイトル色を踏襲する）。 */
const LINK_CARD_TITLE_COLOR = "#1e3a8a";
/** リンクカードの説明文・サイト名の帯の色（表示側の色調 muted に対応する落ち着いた色）。 */
const LINK_CARD_MUTED_COLOR = "#64748b";
/** リンクカードの地色。 */
const LINK_CARD_BACKGROUND = "#eef2ff";
/** リンクカードの画像を読み込めなかったときに画像領域へ敷く塗り（地色より一段暗い同系色）。 */
const LINK_CARD_IMAGE_PLACEHOLDER = "#e0e7ff";

/** 付箋の署名帯の右マージン（stage px。sticky-object.tsx の right: 32px ＋ left: 8px 相当）。 */
const STICKY_SIGNATURE_LEFT_PX = 8;
const STICKY_SIGNATURE_RIGHT_PX = 32;
/** 付箋の署名帯の下端位置（stage px。sticky-object.tsx の bottom: 4px と一致させる）。 */
const STICKY_SIGNATURE_BOTTOM_PX = 4;

/** 絵文字スタンプをグリフとして描く下限サイズ（ラスタ px）。これ未満は判別できないため点で示す。 */
const STAMP_GLYPH_MIN_PX = 3;

/**
 * キャンバスのサムネイル（PNG data URL）を描画する。
 *
 * 文字は実文字ではなくスケルトン帯で表す（canvas-text-skeleton.ts）。1/10 前後の縮尺では
 * 実文字が読めず、可読性のための下限サイズが図形からのはみ出しを生んでいたためである。
 *
 * 戻り値は次の 3 通りで、呼び出し側が「中断」と「失敗」を区別できるようにする。
 * - data URL: 正常終了
 * - `null`: signal による中断（結果を使ってはいけない）
 * - 空文字列: 2D コンテキストを取れなかった、または toDataURL が失敗した
 */
export async function renderCanvasThumbnailDataUrl(
  objects: readonly CanvasObject[],
  // 出力対象のキャンバス矩形（サイズプリセットに応じた原点＋寸法）。矩形外のオブジェクトはラスタ端でクリップされる。
  canvasRect: CanvasFrame,
  options?: {
    readonly signal?: AbortSignal | undefined;
    /** リンクカードの imageUrl を読み込み可能な URL へ解決する（未指定なら読み込まない）。 */
    readonly resolveLinkCardImageUrl?: LinkCardImageResolver | undefined;
  }
): Promise<string | null> {
  const signal = options?.signal;
  const canvas = document.createElement("canvas");
  canvas.width = THUMBNAIL_WIDTH;
  canvas.height = THUMBNAIL_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  const scaleX = THUMBNAIL_WIDTH / canvasRect.w;
  const scaleY = THUMBNAIL_HEIGHT / canvasRect.h;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  drawGrid(ctx, canvasRect, scaleX, scaleY);

  // コメントスレッド（ピン）はビューポート固定の UI 装飾であり、サムネイルには描画しない。
  const drawable = objects.filter((object) => isDrawableObject(object.type));
  // セクション（領域コンテナ）は常に通常オブジェクトの背面へ描画する（Web 表示のサブレイヤ分離と同じ規則）。
  const ordered = drawable.slice().sort(compareCanvasObjectsForRender);
  // コネクタは端点オブジェクトの現在位置から都度ルーティングするため id 索引を渡す。
  const objectsById = new Map(drawable.map((object) => [object.id, object]));
  // サムネイルは S3 経由で他の利用者の端末へ表示されるため、本人ローカルの blob: URL は
  // 読み込まない（他端末では解決できず、描いても意味が無い）。
  // 画像の読み込みは best-effort。1 枚でも失敗するとサムネイル全体が出ないのは退行なので、
  // preloadImages 自体の例外も空の Map へ落とす（個々の画像の失敗は preloadImages 内で
  // 吸収され、該当 src が Map へ入らないことでプレースホルダへフォールバックする）。
  const imageBySrc = await preloadImages(ordered, true, {
    includePlaceholderSrc: true,
    signal,
    resolveLinkCardImageUrl: options?.resolveLinkCardImageUrl,
  }).catch(() => new Map<string, HTMLImageElement>());
  // 中断点 1: 画像を待つ最も長い区間（最大 IMAGE_LOAD_TIMEOUT_MS = 4 秒）の直後。
  // 世代ガードだけでは「完了した結果を捨てる」だけで、以降の全図形の描画と
  // PNG エンコード（CPU を最も使う部分）が旧世代でも走ってしまう。
  if (signal?.aborted) return null;

  for (const object of ordered) {
    ctx.save();
    // キャンバス矩形の原点分だけオフセットして描く（原点が負値の大サイズでも正しく配置される）。
    ctx.translate(
      (object.x - canvasRect.x + object.w / 2) * scaleX,
      (object.y - canvasRect.y + object.h / 2) * scaleY
    );
    ctx.rotate(((object.rotation ?? 0) * Math.PI) / 180);
    ctx.translate((-object.w / 2) * scaleX, (-object.h / 2) * scaleY);
    drawObject(ctx, object, scaleX, scaleY, objectsById, imageBySrc);
    ctx.restore();
  }

  // 中断点 2: PNG エンコードの直前。960×540 のエンコードは同期的で重いため、旧世代では実行しない。
  if (signal?.aborted) return null;
  // toDataURL は巨大なラスタやセキュリティ制約で例外を投げ得る。既存の PDF レンダラと
  // 同じく空文字列へフォールバックし、呼び出し側（フック）が「更新しない」を選べるようにする。
  try {
    return canvas.toDataURL("image/png");
  } catch {
    return "";
  }
}

export function dataUrlToPngBlob(dataUrl: string): Blob | null {
  const [header, body] = dataUrl.split(",");
  if (!header?.startsWith("data:image/png;base64") || !body) return null;
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "image/png" });
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  canvasRect: CanvasFrame,
  scaleX: number,
  scaleY: number
): void {
  // 編集画面のグリッド（canvas-grid）と同様に、キャンバス矩形原点の位相ぶんずらして、サムネイルの
  // グリッド基準位置を編集画面と揃える（大サイズで原点が負値でも一致させる）。
  const phaseX = (((canvasRect.x % CANVAS_GRID_PX) + CANVAS_GRID_PX) % CANVAS_GRID_PX) * scaleX;
  const phaseY = (((canvasRect.y % CANVAS_GRID_PX) + CANVAS_GRID_PX) % CANVAS_GRID_PX) * scaleY;
  ctx.fillStyle = GRID_DOT_COLOR;
  for (let x = -phaseX; x < THUMBNAIL_WIDTH; x += GRID_DOT_STEP_PX) {
    for (let y = -phaseY; y < THUMBNAIL_HEIGHT; y += GRID_DOT_STEP_PX) {
      ctx.fillRect(x, y, 1, 1);
    }
  }
}

function drawObject(
  ctx: CanvasRenderingContext2D,
  object: CanvasObject,
  scaleX: number,
  scaleY: number,
  objectsById: Map<string, CanvasObject>,
  imageBySrc: Map<string, HTMLImageElement>
): void {
  const w = Math.max(1, object.w * scaleX);
  const h = Math.max(1, object.h * scaleY);
  switch (object.type) {
    case "section": {
      // セクションは薄い塗り + 枠線 + 左上タイトルで領域を示す（Web 表示の見た目に合わせる）。
      const fill = object.fill ?? SECTION_DEFAULT_FILL;
      ctx.save();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
      ctx.strokeStyle = fill;
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, w, h);
      drawSectionTitleBar(ctx, displayTextOfLinkifiedText(object.text), w, h, scaleX, scaleY);
      return;
    }
    case "sticky": {
      ctx.fillStyle = object.color ?? "#fff5b3";
      ctx.strokeStyle = "rgba(30, 41, 59, 0.2)";
      ctx.lineWidth = 1;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeRect(0, 0, w, h);
      // 帯の色は背景色に応じた本文色・署名色（濃い背景では白系へ反転）。object.color は付箋の
      // 背景色なので、そのまま帯に使うと背景へ溶け込んで「文字がある」ことが伝わらない。
      const palette = resolveStickyTextPalette(object.color);
      // 署名の有無の判定は、表示側（sticky-object.tsx）・PDF 出力側と同じ真偽で行う。
      // ここだけ trim して判定すると、本文の予約高さが両者と食い違い、帯の行割りがずれる。
      const hasSignature = Boolean(object.updatedByName);
      const padX = STICKY_PADDING_PX * scaleX;
      const padY = STICKY_PADDING_PX * scaleY;
      // 署名がある付箋は、画面表示（sticky-object.tsx の paddingBottom）と同じ予約高さを差し引き、
      // 本文の帯が署名の帯と重ならないようにする。
      const reserveY = hasSignature
        ? computeStickyBodyReservePx({ boxHeight: object.h, boxWidth: object.w }) * scaleY
        : 0;
      const availWidth = w - padX * 2;
      const availHeight = h - padY * 2 - reserveY;
      // 文字寄せ（未指定は左寄せ）を帯へも反映する。反映しないと中央・右寄せの付箋だけ
      // サムネイルが左寄せになり、実表示と食い違う。
      const align = object.textAlign ?? "left";
      if (object.text) {
        // PDF 出力と同一の best-fit。行数からの推定では折り返し位置が実表示とずれる。
        const fontPx = computeStickyExportFontPx(
          ctx,
          object.text,
          availWidth,
          availHeight,
          scaleY,
          align,
          object.tableColumnWidths
        );
        withClip(ctx, 0, 0, w, h, () => {
          drawTextSkeleton(ctx, {
            align,
            areaHeight: availHeight,
            areaWidth: availWidth,
            baseFontPx: fontPx,
            color: palette.body,
            originX: padX,
            originY: padY,
            // 表の外枠線（画面 1px）は文字と同じ倍率で換算する（best-fit の判定と同じ列幅にする）。
            pxScale: scaleY,
            text: object.text,
            ...(object.tableColumnWidths === undefined
              ? {}
              : { tableColumnWidths: object.tableColumnWidths }),
          });
        });
      }
      if (hasSignature) {
        drawStickySignatureBar(
          ctx,
          object.updatedByName ?? "",
          { boxHeight: object.h, boxWidth: object.w },
          w,
          h,
          scaleX,
          scaleY,
          palette.signature
        );
      }
      return;
    }
    case "shape":
      ctx.strokeStyle = object.stroke ?? "#1a1a2e";
      ctx.fillStyle = object.fill ?? "rgba(255,255,255,0)";
      ctx.lineWidth = 1.5;
      applyCanvasStrokePattern(ctx, ctx.lineWidth, object.strokePattern);
      ctx.lineCap = resolveStrokePattern(object.strokePattern) === "dotted" ? "round" : "butt";
      drawShape(ctx, object.shapeKind, w, h, object.arrowFromEdge, object.arrowFromCorner, {
        x: 0,
        y: 0,
        w,
        h,
        radius: resolveRectCornerRadius(w, h, Math.min(scaleX, scaleY)),
      });
      return;
    case "text": {
      const background = resolveTextBackground(object.fill);
      if (background !== undefined) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, w, h);
      }
      // 文字サイズの下限は置かない。帯は文字サイズに比例した長方形なので、0.5px でも
      // 描画自体は成立し（潰れて見えなくなるだけ）、はみ出しが構造的に起きない。
      withClip(ctx, 0, 0, w, h, () => {
        drawTextSkeleton(ctx, {
          align: object.textAlign ?? "left",
          areaHeight: h,
          areaWidth: w,
          baseFontPx: (object.fontSize ?? 16) * scaleY,
          forceBold: object.bold === true,
          color: object.color ?? TEXT_DEFAULT_COLOR,
          originX: 0,
          originY: 0,
          // 表の外枠線（画面 1px）は文字と同じ倍率で換算する。
          pxScale: scaleY,
          text: object.text,
          ...(object.tableColumnWidths === undefined
            ? {}
            : { tableColumnWidths: object.tableColumnWidths }),
        });
      });
      return;
    }
    case "table": {
      const fontPx = Math.max(1, object.fontSize ?? 16);
      // 行高の見積もりには倍率をかける前の列幅（画面と同じ px）を渡す。
      const baseColumnWidths = resolveTableObjectColumnWidths({
        cells: object.cells,
        columnFractions: object.columnFractions,
        availablePx: Math.max(0, object.w - TABLE_BORDER_PX * 2),
        fontPx,
      });
      const columnWidths = baseColumnWidths.map((value) => value * scaleX);
      // 帯の幅と行高は、画面・PDF と同じ表示テキスト（記法を解釈した後の文字列）で測る。
      const cellTexts = object.cells.map((row) => row.map(tableObjectCellDisplayText));
      const rowHeights = resolveTableObjectRowHeights({
        cellTexts,
        columnWidthsPx: baseColumnWidths,
        // 部分書式（run）の文字サイズも行高へ反映し、行境界を画面・PDF とそろえる。
        cellInlineStyles: object.cellInlineStyles,
        availablePx: Math.max(0, object.h - TABLE_BORDER_PX * 2),
        fontPx,
      }).map((value) => value * scaleY);
      withClip(ctx, 0, 0, w, h, () => {
        drawTableSkeleton(ctx, {
          cellTexts,
          columnWidths,
          rowHeights,
          borderWidth: TABLE_BORDER_PX * Math.min(scaleX, scaleY),
          borderColor: MARKDOWN_TABLE_BORDER_COLOR,
          textColor: object.color ?? TEXT_DEFAULT_COLOR,
          fontPx: fontPx * scaleY,
          paddingX: (TABLE_CELL_PADDING_EM / 2) * fontPx * scaleX,
        });
      });
      return;
    }
    case "image": {
      // 本体画像を読めないときは、画面表示（image-object.tsx）と同じく永続化された LQIP
      // （placeholderSrc。data: URL のため CORS の制約を受けない）へフォールバックする。
      const img =
        (object.src ? imageBySrc.get(object.src) : undefined) ??
        (object.placeholderSrc ? imageBySrc.get(object.placeholderSrc) : undefined);
      if (img) {
        // 表示側は objectFit: "contain"。contain は定義上枠内へ収まるためクリップは要らない。
        const fit = fitContain(img.naturalWidth, img.naturalHeight, w, h);
        ctx.drawImage(img, fit.x, fit.y, fit.w, fit.h);
      } else {
        drawMediaPlaceholder(ctx, w, h, "#dbeafe");
      }
      return;
    }
    case "video":
      // 動画はフレーム抽出に読み込みとシークが必要で生成時間が読めないため、プレースホルダを維持する。
      drawMediaPlaceholder(ctx, w, h, "#111827");
      return;
    case "link-card":
      drawLinkCard(ctx, object, w, h, scaleX, scaleY, imageBySrc);
      return;
    case "pen-stroke": {
      ctx.strokeStyle = object.color;
      ctx.lineWidth = Math.max(1, object.thickness * scaleX);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      applyCanvasStrokePattern(ctx, ctx.lineWidth, object.strokePattern);
      ctx.beginPath();
      const outputPoints = object.points.map((point) => ({
        x: (point.x - object.x) * scaleX,
        y: (point.y - object.y) * scaleY,
      }));
      outputPoints.forEach((point, index) => {
        if (index === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
      fillDottedStrokeStartFallback(
        ctx,
        outputPoints,
        ctx.lineWidth,
        object.strokePattern,
        object.color
      );
      return;
    }
    case "stamp": {
      // アバタースタンプは、プリロード済みの実アバター画像を描いてキャンバス本体と見た目を一致させる。
      // 描画直前にも許可判定を行い、stamp-object（DOM 描画）と完全一致させる
      // （安全なラスタ data:image/ のみ描画、それ以外は 👤 へフォールバック）。
      if (object.stampKind === "avatar") {
        const avatarImg =
          isSnapshotAvatarImageUrl(object.avatarSrc) && object.avatarSrc
            ? imageBySrc.get(object.avatarSrc)
            : undefined;
        if (avatarImg) {
          ctx.save();
          ctx.beginPath();
          // 画面側（stamp-object）は border-radius: 50% で、非正方形（w!==h）のとき楕円クリップになる。
          ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          // 表示側は objectFit: "cover"。枠より大きくなるためクリップしてから描く。
          const fit = fitCover(avatarImg.naturalWidth, avatarImg.naturalHeight, w, h);
          ctx.drawImage(avatarImg, fit.x, fit.y, fit.w, fit.h);
          ctx.restore();
          return;
        }
      }
      // 絵文字スタンプ（およびアバター画像が取得できなかった場合）はグリフを中央に描画する。
      // スタンプは「文字」ではなく図形記号なので、スケルトン帯の対象にはしない。
      const glyph =
        object.stampKind === "avatar" ? "👤" : (STAMP_THUMB_GLYPH[object.stampKind] ?? "❔");
      const sizePx = Math.min(w, h) * 0.85;
      ctx.fillStyle = "#1a1a2e";
      if (sizePx < STAMP_GLYPH_MIN_PX) {
        // 極小サイズでは絵文字を判別できないため、位置だけを示す点（円）へ置き換える。
        ctx.beginPath();
        ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
        return;
      }
      ctx.font = `${sizePx}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(glyph, w / 2, h / 2);
      ctx.textAlign = "start";
      ctx.textBaseline = "alphabetic";
      return;
    }
    case "connector": {
      const fromObj = objectsById.get(object.from.objectId);
      const toObj = objectsById.get(object.to.objectId);
      if (!fromObj || !toObj) return;
      // 画面・PDF と同じ共有の幾何（線の形・経路・破線・両端の形状）で描く。
      const geometry = computeConnectorGeometry(
        connectorGeometryInputFor(object, connectorFrameOf(fromObj), connectorFrameOf(toObj))
      );
      // 端点が一致する配置（線の長さ 0）でも、画面と同じく両端の形状を同じ位置へ描く。
      // ルートはステージ座標。ctx は object.x/object.y へ平行移動済みのため相対座標へ変換する。
      drawConnectorGeometry(ctx, geometry, {
        color: object.stroke ?? CONNECTOR_DEFAULT_STROKE,
        map: (p) => ({ x: (p.x - object.x) * scaleX, y: (p.y - object.y) * scaleY }),
        scale: Math.min(scaleX, scaleY),
        // サムネイルは従来どおり、保存線幅にかかわらず縮小率に応じた固定の線幅で描く。
        lineWidth: Math.max(1, 1.5 * Math.min(scaleX, scaleY)),
      });
      return;
    }
  }
}

// サムネイル描画用のスタンプ種別 → 絵文字グリフ対応表。
// stamp-object.tsx の STAMP_KIND_GLYPH と同じ値を持つが、循環参照回避のため複製している。
// 型は EmojiStampKind 基準にして、絵文字種別の増減にキー追従が必要になるようにする（avatar は対象外）。
const STAMP_THUMB_GLYPH: Record<EmojiStampKind, string> = {
  "thumbs-up": "👍",
  heart: "❤️",
  party: "🎉",
  grin: "😄",
  surprised: "😮",
  pray: "🙏",
  eyes: "👀",
  bulb: "💡",
  check: "✅",
  rocket: "🚀",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
  cry: "😢",
  sob: "😭",
  disappointed: "😞",
  pensive: "😔",
  "broken-heart": "💔",
};

/**
 * 矩形へクリップした状態で描画する。
 *
 * クリップは文字のスケルトンと cover フィットの画像にだけ掛ける。drawObject 全体へ掛けると、
 * 矩形の境界の外側にも線幅の半分を描く strokeRect が半分に欠け、端点移動後に再計算される
 * コネクタのルートも保存枠の外で切れる（いずれも既存の見た目を壊す退行になる）。
 */
function withClip(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  draw: () => void
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();
  draw();
  ctx.restore();
}

/**
 * セクションのタイトル帯を描く。
 * 表示側（section-object.tsx）は上部の固定高さのタイトルバー内に単一行で描き、折り返さない。
 */
function drawSectionTitleBar(
  ctx: CanvasRenderingContext2D,
  title: string,
  w: number,
  h: number,
  scaleX: number,
  scaleY: number
): void {
  if (title.length === 0) return;
  const fontPx = SECTION_TITLE_FONT_PX * scaleY;
  const barHeight = textBarHeightFor(fontPx);
  if (barHeight <= 0) return;
  const left = SECTION_TITLE_PADDING_X_PX * scaleX;
  const maxWidth = w - SECTION_TITLE_PADDING_X_PX * 2 * scaleX;
  if (maxWidth <= 0) return;
  // 表示側は fontWeight: 600。Canvas 2D の指定は bold で近似する。
  ctx.font = `bold ${fontPx}px sans-serif`;
  const width = Math.min(maxWidth, ctx.measureText(title).width);
  // タイトルバー（高さ 32px）の縦中心へ置く。
  const centerY = (SECTION_TITLE_BAR_HEIGHT_PX / 2) * scaleY;
  withClip(ctx, 0, 0, w, h, () => {
    drawSingleTextBar(ctx, {
      color: SECTION_TITLE_COLOR,
      height: barHeight,
      width,
      x: left,
      y: centerY - barHeight / 2,
    });
  });
}

/**
 * 付箋の最終更新者名（左下の署名）の帯を描く。
 * 表示側（sticky-object.tsx）の left: 8px / bottom: 4px / right: 32px に対応させる
 * （右マージンはめくれ三角ぶん）。
 */
function drawStickySignatureBar(
  ctx: CanvasRenderingContext2D,
  name: string,
  box: { readonly boxWidth: number; readonly boxHeight: number },
  w: number,
  h: number,
  scaleX: number,
  scaleY: number,
  color: string
): void {
  const fontPx = computeStickySignatureFontPx(box) * scaleY;
  const barHeight = textBarHeightFor(fontPx);
  if (barHeight <= 0) return;
  const left = STICKY_SIGNATURE_LEFT_PX * scaleX;
  const maxWidth = w - (STICKY_SIGNATURE_LEFT_PX + STICKY_SIGNATURE_RIGHT_PX) * scaleX;
  if (maxWidth <= 0) return;
  ctx.font = `${fontPx}px sans-serif`;
  const width = Math.min(maxWidth, ctx.measureText(name).width);
  const top = h - STICKY_SIGNATURE_BOTTOM_PX * scaleY - barHeight;
  withClip(ctx, 0, 0, w, h, () => {
    drawSingleTextBar(ctx, { color, height: barHeight, width, x: left, y: top });
  });
}

/**
 * リンクカードを描く。
 * 表示側（link-card-object.tsx）は Markdown を解釈せず、画像（任意）＋ タイトル / 説明文（任意）/
 * サイト名の固定 3 要素を縦に並べる。サムネイルもこの構成へそろえる。
 */
function drawLinkCard(
  ctx: CanvasRenderingContext2D,
  object: Extract<CanvasObject, { type: "link-card" }>,
  w: number,
  h: number,
  scaleX: number,
  scaleY: number,
  imageBySrc: Map<string, HTMLImageElement>
): void {
  drawMediaPlaceholder(ctx, w, h, LINK_CARD_BACKGROUND);
  // 表示側は imageUrl があれば読み込みの成否にかかわらず高さ 55% の <img> を置く。
  // 読み込めなかったときに領域を詰めると、文字帯の位置が実表示とずれるため詰めない。
  const imageHeight = object.imageUrl ? h * LINK_CARD_IMAGE_HEIGHT_RATIO : 0;
  if (object.imageUrl) {
    const img = imageBySrc.get(object.imageUrl);
    if (img) {
      withClip(ctx, 0, 0, w, imageHeight, () => {
        // 表示側は objectFit: "cover"。枠より大きくなるためクリップしてから描く。
        const fit = fitCover(img.naturalWidth, img.naturalHeight, w, imageHeight);
        ctx.drawImage(img, fit.x, fit.y, fit.w, fit.h);
      });
    } else {
      ctx.fillStyle = LINK_CARD_IMAGE_PLACEHOLDER;
      ctx.fillRect(0, 0, w, imageHeight);
    }
  }

  const padX = LINK_CARD_PADDING_PX * scaleX;
  const padY = LINK_CARD_PADDING_PX * scaleY;
  const areaWidth = w - padX * 2;
  const areaBottom = h - padY;
  if (areaWidth <= 0) return;
  const gap = LINK_CARD_GAP_PX * scaleY;
  // タイトルとサイト名は表示側が nowrap（単一行）。説明文だけが折り返す。
  const rows: readonly LinkCardTextRow[] = [
    {
      bold: true,
      color: LINK_CARD_TITLE_COLOR,
      fontPx: LINK_CARD_TITLE_FONT_PX * scaleY,
      text: object.title,
      wrap: false,
    },
    ...(object.description
      ? [
          {
            bold: false,
            color: LINK_CARD_MUTED_COLOR,
            fontPx: LINK_CARD_DESCRIPTION_FONT_PX * scaleY,
            text: object.description,
            wrap: true,
          } as const,
        ]
      : []),
    {
      bold: false,
      color: LINK_CARD_MUTED_COLOR,
      fontPx: LINK_CARD_SITE_NAME_FONT_PX * scaleY,
      // サイト名は siteName ?? safeHostname(url) で常に出る（表示側と同じ解決）。
      text: object.siteName ?? safeHostname(object.url),
      wrap: false,
    },
  ];

  withClip(ctx, 0, 0, w, h, () => {
    let cursorY = imageHeight + padY;
    for (const [index, row] of rows.entries()) {
      if (index > 0) cursorY += gap;
      if (row.fontPx <= 0) continue;
      if (row.text.length === 0) {
        // 表示側は空文字の要素でも 1 行分の高さを占めるため、帯は描かずに位置だけ送る。
        cursorY += row.fontPx * STICKY_LINE_HEIGHT_RATIO;
        continue;
      }
      ctx.font = `${row.bold ? "bold " : ""}${row.fontPx}px sans-serif`;
      const barHeight = textBarHeightFor(row.fontPx);
      const lineHeight = row.fontPx * STICKY_LINE_HEIGHT_RATIO;
      const lines = row.wrap ? wrapTextToWidth(ctx, row.text, areaWidth) : [row.text];
      for (const line of lines) {
        // ベースラインが文字領域の下端を超えたら、以降の行も要素も入らないので打ち切る
        // （表示側もカード外へは出ない）。
        if (cursorY + row.fontPx > areaBottom) return;
        const width = Math.min(areaWidth, ctx.measureText(line).width);
        drawSingleTextBar(ctx, {
          color: row.color,
          height: barHeight,
          width,
          x: padX,
          y: cursorY + row.fontPx - barHeight,
        });
        cursorY += lineHeight;
      }
    }
  });
}

/** リンクカードの文字要素 1 つぶんの描画指定。 */
interface LinkCardTextRow {
  readonly bold: boolean;
  readonly color: string;
  readonly fontPx: number;
  readonly text: string;
  /** 折り返すか（表示側が nowrap の要素は false）。 */
  readonly wrap: boolean;
}

/**
 * 1 論理行を幅へ収まるよう 1 文字単位で折り返す（日本語 / 英語混在向けの詰め込み方式）。
 * 呼び出し前に ctx.font を設定しておくこと。1 文字も入らない幅でもその 1 文字は必ず出力する
 * （無限ループ防止。帯の幅は呼び出し側で areaWidth へクランプされる）。
 */
function wrapTextToWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): readonly string[] {
  const wrapped: string[] = [];
  let current = "";
  for (const character of text) {
    const candidate = current + character;
    if (ctx.measureText(candidate).width > maxWidth && current.length > 0) {
      wrapped.push(current);
      current = character;
    } else {
      current = candidate;
    }
  }
  wrapped.push(current);
  return wrapped;
}

/** 縦横比を保って枠内へ収める矩形を返す（画像オブジェクトの objectFit: contain と一致）。 */
function fitContain(
  imgWidth: number,
  imgHeight: number,
  boxWidth: number,
  boxHeight: number
): { x: number; y: number; w: number; h: number } {
  if (imgWidth <= 0 || imgHeight <= 0) return { h: boxHeight, w: boxWidth, x: 0, y: 0 };
  const scale = Math.min(boxWidth / imgWidth, boxHeight / imgHeight);
  const w = imgWidth * scale;
  const h = imgHeight * scale;
  return { h, w, x: (boxWidth - w) / 2, y: (boxHeight - h) / 2 };
}

/**
 * 縦横比を保って枠を覆う矩形を返す（リンクカード画像・アバタースタンプの objectFit: cover と一致）。
 * 枠より大きくなるため、呼び出し側で枠へクリップしてから描く。
 */
function fitCover(
  imgWidth: number,
  imgHeight: number,
  boxWidth: number,
  boxHeight: number
): { x: number; y: number; w: number; h: number } {
  if (imgWidth <= 0 || imgHeight <= 0) return { h: boxHeight, w: boxWidth, x: 0, y: 0 };
  const scale = Math.max(boxWidth / imgWidth, boxHeight / imgHeight);
  const w = imgWidth * scale;
  const h = imgHeight * scale;
  return { h, w, x: (boxWidth - w) / 2, y: (boxHeight - h) / 2 };
}

function drawShape(
  ctx: CanvasRenderingContext2D,
  shapeKind: "arrow" | "circle" | "rectangle",
  w: number,
  h: number,
  arrowFromEdge: ArrowEdge | undefined,
  arrowFromCorner: ArrowCorner | undefined,
  rect: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
    readonly radius: number;
  }
): void {
  if (shapeKind === "circle") {
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, Math.max(1, w / 2 - 1), Math.max(1, h / 2 - 1), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    return;
  }
  if (shapeKind === "arrow") {
    // 軸そろえ矢印は起点辺→対辺、対角矢印は起点角→対角へ描く。
    if (arrowFromEdge || arrowFromCorner) {
      const path = arrowFromEdge
        ? resolveArrowEdgePath(w, h, arrowFromEdge, 8, 4)
        : resolveArrowPath(w, h, arrowFromCorner!, 8, 4);
      ctx.beginPath();
      ctx.moveTo(path.tail.x, path.tail.y);
      ctx.lineTo(path.head.x, path.head.y);
      ctx.moveTo(path.wingA.x, path.wingA.y);
      ctx.lineTo(path.head.x, path.head.y);
      ctx.lineTo(path.wingB.x, path.wingB.y);
      ctx.stroke();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(2, h / 2);
    ctx.lineTo(w - 6, h / 2);
    ctx.moveTo(w - 8, h / 2 - 4);
    ctx.lineTo(w - 2, h / 2);
    ctx.lineTo(w - 8, h / 2 + 4);
    ctx.stroke();
    return;
  }
  if (rect.w <= 0 || rect.h <= 0 || rect.radius < 0.5) {
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    return;
  }
  ctx.beginPath();
  traceRoundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.fill();
  ctx.stroke();
}

function drawMediaPlaceholder(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  color: string
): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = "rgba(30, 41, 59, 0.2)";
  ctx.lineWidth = 1;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeRect(0, 0, w, h);
}
